import { SuggestedQuestion } from './actions';

interface GlobalAskAicaService {
    /**
     * Opens the AskAica modal and sends the provided message
     * @param message - The message/question to send to AskAica
     */
    openModalWithMessage: (message: string) => void;

    /**
     * Registers an AskAica instance with the service
     * This should be called by the global AskAicaV2 component when it mounts
     * @param openModalFn - Function to open the AskAica modal
     * @param sendMessageFn - Function to send a message to the AskAica chat
     */
    registerAskAicaInstance: (
        openModalFn: (message?: string) => void,
        sendMessageFn: (message: string, sources?: string[]) => void,
    ) => void;
}

class AskAicaServiceImpl implements GlobalAskAicaService {
    /**
     * Reference to the function that opens the AskAica modal
     * This is set when the AskAicaV2 component registers itself with the service
     */
    private openModal: ((message?: string) => void) | null = null;

    /**
     * Reference to the function that sends a message to the AskAica chat
     * This is set when the AskAicaV2 component registers itself with the service
     */
    private sendMessage: ((message: string, sources?: string[]) => void) | null = null;

    /**
     * Registers an AskAica instance with the service
     * This should be called by the global AskAicaV2 component when it mounts
     *
     * @param openModalFn - Function to open the AskAica modal
     * @param sendMessageFn - Function to send a message to the AskAica chat
     */
    registerAskAicaInstance(
        openModalFn: ((message?: string) => void) | null,
        sendMessageFn: ((message: string, sources?: string[]) => void) | null,
    ): void {
        this.openModal = openModalFn;
        this.sendMessage = sendMessageFn;
    }

    /**
     * Opens the AskAica modal and sends the provided message
     * This is the main method used by ModuleSuggestedQuestion components
     *
     * @param message - The message/question to send to AskAica
     */
    openModalWithMessage(message: string): void {
        if (this.openModal && this.sendMessage) {
            // First open the modal with the message
            // The AskAicaV2 component will handle sending the message once the modal is open
            this.openModal(message);
        } else {
            console.error('AskAica instance not registered. Make sure the global AskAicaV2 component is mounted.');
        }
    }
}

/**
 * Singleton instance of the AskAicaService
 * This is the main export that should be used throughout the application
 * There should only be one instance of this service to ensure proper communication
 */
export const globalAskAicaService = new AskAicaServiceImpl();

/**
 * Helper function to handle suggested question clicks
 * This is used by the ModuleSuggestedQuestion component to send questions to AskAica
 *
 * @param question - The question to send (can be a string or a SuggestedQuestion object)
 */
export const handleSuggestedQuestionClick = (question: string | SuggestedQuestion): void => {
    // Extract the text from the question (handles both string and SuggestedQuestion types)
    const text = typeof question === 'string' ? question : question.text;

    // Use the global service to open the modal and send the message
    globalAskAicaService.openModalWithMessage(text);
};
